import * as React from 'react';
import styled from 'styled-components';
import { type Row, flexRender, type RowData } from 'src/lib/tanstack-react-table';
import { IconButton } from 'src/components/atoms/IconButton';
import { ChevronDown } from 'src/assets/icons';
import Collapse from '@mui/material/Collapse';
import { TableRow, TableCell } from './atoms';
import { TableProps } from './table-type';

const StyledIconButton = styled(IconButton)<{
  $isExpanded: boolean;
}>`
  svg {
    transform: ${(props) =>
      props.$isExpanded ? 'rotate(180deg) translate(0)' : 'rotate(0deg) translate(0)'} !important;
    transition: transform 0.3s ease;
  }
`;

export interface CollapsibleRowProps<TTableData extends RowData> {
  row: Row<TTableData>;
  CollapsibleContent: NonNullable<TableProps<TTableData>['CollapsibleContent']>;
  handleRowClick?: (e: React.SyntheticEvent<HTMLTableRowElement>) => void;
}

export const CollapsibleRow = <TTableData extends RowData>({
  row,
  CollapsibleContent,
  handleRowClick = undefined,
}: CollapsibleRowProps<TTableData>) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  const toggleExpanded = (e: React.MouseEvent<unknown>) => {
    e.stopPropagation();
    setIsExpanded((prevState) => !prevState);
  };

  const rowId = (row?.original as { id?: string })?.id || '';

  return (
    <>
      <TableRow
        data-id={rowId}
        $isBody
        onClick={typeof handleRowClick === 'function' ? handleRowClick : toggleExpanded}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} $size={cell.column.getSize()} $isExpanded={isExpanded}>
            {cell.column.columnDef.id === 'collapsible' ? (
              <StyledIconButton
                $isExpanded={isExpanded}
                title="Expand row"
                aria-labelledby="Expand row"
                onClick={toggleExpanded}
              >
                <ChevronDown />
              </StyledIconButton>
            ) : (
              flexRender(cell.column.columnDef.cell, cell.getContext())
            )}
          </TableCell>
        ))}
      </TableRow>

      <TableRow>
        <TableCell $isExpanded={isExpanded} style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <CollapsibleContent row={row} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
